import React, { Fragment, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import ProfileCardTournament from '../../components/ProfilecardTournament';
import CategoryProfileTabs, { CategoryTabId } from '../../components/CategoryProfileTabs';
import CategorySobreTab from '../../components/CategorySobreTab';
import CategoryJogosTab from '../../components/CategoryJogosTab';
import CategoryInscritosTab from '../../components/CategoryInscritosTab';
import {
  CATEGORIA_TORNEIO_POR_ID,
  CategoriaTorneioPorIdData,
} from '../../services/categoriaTorneioService';
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
  CONTAGEM_VAGAS_POR_CATEGORIA,
  ContagemVagasData,
  ContagemVagasVars,
} from '../../services/torneioService';
import {
  FASE_GRUPO_PARTIDAS_POR_FASE_CATEGORIA_TORNEIO,
  FaseGrupoPartidasPorFaseCategoriaTorneioData,
  FaseGrupoPartidasPorFaseCategoriaTorneioVars,
} from '../../services/faseGrupoPartidasService';
import {
  EQUIPES_POR_CATEGORIA_TORNEIO,
  EquipesPorCategoriaTorneioData,
  EquipesPorCategoriaTorneioVars,
} from '../../services/equipeService';
import { temFaseDeGrupos } from '../../utils/temFaseDeGrupos';

const TournamentCategoryPage: React.FC = () => {
  const { torneioId, categoriaId } = useParams<{ torneioId: string; categoriaId: string }>();

  // Hash-based tab state management
  const location = useLocation();
  const navigate = useNavigate();

  const hashToTab = (hash: string): CategoryTabId => {
    if (hash === '#jogos') return 'jogos';
    if (hash === '#inscritos') return 'inscritos';
    return 'sobre';
  };

  const [activeTab, setActiveTabState] = useState<CategoryTabId>(() =>
    hashToTab(location.hash)
  );

  useEffect(() => {
    setActiveTabState(hashToTab(location.hash));
  }, [location.hash]);

  const onTabChange = (tab: CategoryTabId) => {
    setActiveTabState(tab);
    const hash = tab === 'sobre' ? '' : `#${tab}`;
    navigate({ pathname: location.pathname, hash }, { replace: true });
  };

  // Data fetching
  const { data: categoriaData, loading: categoriaLoading, error: categoriaError } =
    useQuery<CategoriaTorneioPorIdData>(CATEGORIA_TORNEIO_POR_ID, {
      variables: { id: categoriaId || '' },
      skip: !categoriaId,
      fetchPolicy: 'no-cache',
    });

  const { data: torneioData, loading: torneioLoading } =
    useQuery<TorneioPorIdData, TorneioPorIdVars>(TORNEIO_POR_ID, {
      variables: { id: torneioId || '' },
      skip: !torneioId,
    });

  const { data: contagemVagasData } = useQuery<ContagemVagasData, ContagemVagasVars>(
    CONTAGEM_VAGAS_POR_CATEGORIA,
    {
      variables: { torneioId: torneioId || '' },
      skip: !torneioId,
    }
  );
  const contagemVagas = contagemVagasData?.contagemVagasPorCategoria || [];

  const categoria = categoriaData?.categoriaTorneioPorId;
  const torneio = torneioData?.torneioPorId;

  // Phase routing logic
  const faseGrupo = categoria?.fasesCategoria?.find(f => f.tipoGrupoChave === 'GRUPO') || null;
  const fasesChave = categoria?.fasesCategoria?.filter(f => f.tipoGrupoChave === 'CHAVE') || [];
  const showGroupPhase = temFaseDeGrupos(categoria?.modeloDisputa);

  // Group phase data
  const { data: gruposData, loading: gruposLoading, error: gruposError } = useQuery<
    FaseGrupoPartidasPorFaseCategoriaTorneioData,
    FaseGrupoPartidasPorFaseCategoriaTorneioVars
  >(FASE_GRUPO_PARTIDAS_POR_FASE_CATEGORIA_TORNEIO, {
    variables: { faseCategoriaTorneioId: faseGrupo?.id || '' },
    skip: !faseGrupo?.id,
    fetchPolicy: 'no-cache',
  });
  const grupos = gruposData?.faseGrupoPartidasPorFaseCategoriaTorneio || [];

  const fasesChaveOrdenadas = [...fasesChave].sort((a, b) => a.ordem - b.ordem);

  // Enrolled teams data
  const { data: equipesData, loading: equipesLoading, error: equipesError } = useQuery<
    EquipesPorCategoriaTorneioData,
    EquipesPorCategoriaTorneioVars
  >(EQUIPES_POR_CATEGORIA_TORNEIO, {
    variables: { categoriaTorneioId: categoriaId || '' },
    skip: !categoriaId,
    fetchPolicy: 'no-cache',
  });
  const equipes = equipesData?.equipesPorCategoriaTorneio || [];

  // Redirect away from inscritos tab if flag is falsy
  useEffect(() => {
    if (activeTab === 'inscritos' && torneio && !torneio.exibirInscritos) {
      onTabChange('sobre');
    }
  }, [activeTab, torneio]);

  const isLoading = categoriaLoading || torneioLoading;

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="row justify-content-center">
              <div className="col-xl-10 col-xxl-10 col-lg-10">
                {isLoading && <Load />}

                {categoriaError && (
                  <div className="alert alert-danger" role="alert">
                    Erro ao carregar categoria.
                  </div>
                )}

                {!isLoading && !categoriaError && !categoria && (
                  <div className="alert alert-warning" role="alert">
                    Categoria não encontrada.
                  </div>
                )}

                {torneio && <ProfileCardTournament torneio={torneio} />}

                {categoria && (
                  <CategoryProfileTabs
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    categoryName={categoria.nome}
                    torneioUrl={`/tournamentsingle/${torneioId}`}
                    exibirInscritos={torneio?.exibirInscritos}
                    sobreContent={
                      <CategorySobreTab
                        categoria={categoria}
                        vagasOcupadas={contagemVagas.find(c => c.categoriaId === categoriaId)?.ocupadas}
                        exibirInscritos={torneio?.exibirInscritos}
                      />
                    }
                    jogosContent={
                      <CategoryJogosTab
                        grupos={grupos}
                        fasesChaveOrdenadas={fasesChaveOrdenadas}
                        gruposLoading={gruposLoading}
                        showGroupPhase={showGroupPhase}
                        faseGrupo={faseGrupo}
                        error={!!gruposError}
                      />
                    }
                    inscritosContent={
                      <CategoryInscritosTab
                        equipes={equipes}
                        loading={equipesLoading}
                        error={!!equipesError}
                        onAtletaClick={(atletaId) => navigate(`/userpage/${atletaId}`)}
                      />
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Appfooter />
    </Fragment>
  );
};

export default TournamentCategoryPage;
